'use client'
import Image from 'next/image'
import LifecycleLink from '@/components/common/LifecycleLink'
import clsx from 'clsx'
import { Avatar, Tooltip } from 'antd'
import { motion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styles from './Character.module.css'
import { Character, CharacterStats } from 'Types/character'
import { User } from 'Types/user'
import { formatNumber } from 'Utils/func'
import { useRecoilValue } from 'recoil'
import { userState } from 'Store/user'
import {
  getAnimatedImageDisplayUrl,
  transformImage,
  isVideoUrl,
} from '@/utils/img'
import { DEFAULT_CHARACTER_LIST_IMAGE_WIDTH } from '@/constant/img'
import { toHomeCharacterPlainTextPreview } from '@/utils/homeCardPreview'
import useI18n from 'Hooks/useI18n'
import { withLocale } from '@/locales'
import CharacterWinnerBadgeIcon, {
  CharacterWinnerBadgeGuard,
} from '@/components/common/CharacterWinnerBadgeIcon'
import HomeCardVideo from '@/components/home/HomeCardVideo'
import { DEFAULT_LOCALE } from '@/constant/lang'
import { TagID } from '@/types/tag'
import { useQuickTracking } from '@/provider/QuickTrackingProvider'
import { getPlatform } from '@/utils/platform'
import { resolveTagWatermark } from '@/utils/tagDisplay'
import { getLocalizedSimulatorGamePath } from '@/utils/simulatorGameNavigation'
import type { NavigateSimulatorGameHandler } from '@/hooks/simulator-game/useNavigateSimulatorGame'
import useTrackOnceInView from 'Hooks/useTrackOnceInView'
import {
  trackSimulatorCardClick,
  trackSimulatorCardExposure,
} from '@/utils/simulatorGameTracking'
import { usePagePerformanceLifecycle } from '@/hooks/usePagePerformanceLifecycle'
import { evictHomeMediaFromPersistentCache } from '@/packages/sw-prefetch/src/prefetch/homeMediaCacheRegistration'

type CharacterItemProps = {
  character: Character
  characterStats: CharacterStats
  creator: User
  fromScreen?: string
  logparams?: {
    gender: string
    selectedTags: TagID[] | string[]
    sorting?: string
  }
  onNavigateSimulatorGame?: NavigateSimulatorGameHandler
  onMediaReady?: () => void
  /**
   * 首行卡片媒体提优先级：eager + fetchPriority=high。
   * 列表页指标终点是「卡片媒体全就绪」，默认 lazy 会把首屏 LCP 候选
   * 推迟到与几十张图同优先级排队；只对首行（index < columns）开启。
   */
  mediaPriority?: boolean
}
export default function CharacterHomeItem({
  character,
  characterStats,
  creator,
  fromScreen,
  logparams,
  onNavigateSimulatorGame,
  onMediaReady,
  mediaPriority = false,
}: CharacterItemProps) {
  const hoverExitDelayMs = 500
  const { t, lang } = useI18n()
  const { trackEvent } = useQuickTracking()
  const pageLifecycle = usePagePerformanceLifecycle()
  const platform = getPlatform()
  const [isHoverExitDelayed, setIsHoverExitDelayed] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const textRef = useRef<HTMLDivElement>(null)
  const bgRef = useRef<HTMLDivElement>(null)
  const hoverExitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const user = useRecoilValue(userState)
  const isGame = character.character_type === 9
  const hideDescriptionPreview = character.content_type === 2
  const descriptionPreview = useMemo(
    () =>
      toHomeCharacterPlainTextPreview(
        character.introduction,
        character.nickname,
        character.is_translated ? lang : DEFAULT_LOCALE
      ),
    [character.introduction, character.is_translated, character.nickname, lang]
  )
  const isVideoMedia = isVideoUrl(character.animated_image_url)
  // 非视频的 animated_image_url 视为动图，单独用原生 img 渲染
  const isAnimatedImageMedia = !!character.animated_image_url && !isVideoMedia
  const imageMediaUrl = getAnimatedImageDisplayUrl(
    character.animated_image_url,
    character.image_url,
    DEFAULT_CHARACTER_LIST_IMAGE_WIDTH,
    90
  )
  const primaryMediaUrl = isVideoMedia
    ? character.animated_image_url
    : imageMediaUrl
  const mediaReadyRef = useRef({
    key: '',
    ready: false,
  })
  const mediaReadyKey = `${character.character_id}:${primaryMediaUrl}`
  if (mediaReadyRef.current.key !== mediaReadyKey) {
    mediaReadyRef.current = {
      key: mediaReadyKey,
      ready: false,
    }
  }
  const handleMediaReady = useCallback(() => {
    if (mediaReadyRef.current.ready) return
    mediaReadyRef.current.ready = true
    onMediaReady?.()
  }, [onMediaReady])
  const handleMediaError = useCallback(() => {
    evictHomeMediaFromPersistentCache(primaryMediaUrl)
    handleMediaReady()
  }, [handleMediaReady, primaryMediaUrl])
  useEffect(() => {
    if (!primaryMediaUrl) handleMediaReady()
  }, [handleMediaReady, primaryMediaUrl])
  const trackSimulatorExposure = useCallback(() => {
    if (!isGame) return
    trackSimulatorCardExposure(trackEvent, {
      simulatorId: character.character_id,
      creatorId: creator.user_id,
      pageName: 'discover',
    })
  }, [character.character_id, creator.user_id, isGame, trackEvent])
  const simulatorExposureRef = useTrackOnceInView({
    enabled: isGame && !!character.character_id,
    onEnterView: trackSimulatorExposure,
    trackKey: `discover:${character.character_id}`,
  })
  const handleHoverStart = () => {
    if (hoverExitTimerRef.current) {
      clearTimeout(hoverExitTimerRef.current)
      hoverExitTimerRef.current = null
    }

    setIsHoverExitDelayed(true)

    const text = textRef.current
    const container = containerRef.current
    if (!text || !container) return
    const textToTop = text.getBoundingClientRect().top
    const containerToTop = container.getBoundingClientRect().top
    // 行高是16px，为了在行数非常多的清空下，显示整数行的内容
    textRef.current.style.maxHeight = `${(((textToTop - containerToTop + 7) / 16) | 0) * 16}px`
  }
  const handleHoverEnd = () => {
    if (hoverExitTimerRef.current) {
      clearTimeout(hoverExitTimerRef.current)
    }

    hoverExitTimerRef.current = setTimeout(() => {
      const text = textRef.current
      hoverExitTimerRef.current = null
      setIsHoverExitDelayed(false)
      if (!text) return
      text.style.maxHeight = '3rem'
    }, hoverExitDelayMs)
  }
  // 直接使用接口返回的 tags 数组判断，不依赖 TagMap
  const chatPath = isGame
    ? getLocalizedSimulatorGamePath({
        gameId: character.character_id,
        lang,
      })
    : character?.content_type === 2
      ? `/custom-chat/${character.character_id}`
      : `/chat/${character.character_id}`
  const cardHref = isGame ? chatPath : withLocale(chatPath, lang)
  const watermarkUrl = resolveTagWatermark(character.tags)
  useEffect(() => {
    const reportCharacterPageExposure = () => {
      trackEvent(
        'character_page_exposure',
        {
          scene: fromScreen,
          type: 'character',
          characterId: character.character_id,
          creatorId: creator.user_id,
          nsfw: !!character.nsfw,
          isStory: false,
          filter: logparams,
          banner: '',
          platform,
        },
        'EXP'
      )
    }

    if (!pageLifecycle) {
      reportCharacterPageExposure()
      return
    }

    return pageLifecycle.subscribeInteractive(reportCharacterPageExposure)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [character.character_id, pageLifecycle])
  useEffect(() => {
    return () => {
      if (hoverExitTimerRef.current) {
        clearTimeout(hoverExitTimerRef.current)
      }
    }
  }, [])
  const handleCardClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    trackEvent(
      'character_page_click',
      {
        scene: fromScreen,
        type: 'character',
        characterId: character.character_id,
        creatorId: creator.user_id,
        nsfw: !!character.nsfw,
        isStory: false,
        filter: logparams,
        banner: '',
        platform,
      },
      'CLK'
    )

    if (!isGame) return

    trackSimulatorCardClick(trackEvent, {
      simulatorId: character.character_id,
      creatorId: creator.user_id,
      pageName: 'discover',
    })
    event.preventDefault()
    void onNavigateSimulatorGame?.({
      gameId: character.character_id,
      mode: 'desktop',
    })
  }

  return (
    <div
      ref={simulatorExposureRef}
      className={clsx(
        'relative w-full overflow-hidden rounded-lg border border-tertiary-border bg-secondary-bg pt-[167.57%]'
      )}
    >
      <LifecycleLink
        href={cardHref}
        prefetch
        className="absolute inset-0"
        onClick={handleCardClick}
      >
        {watermarkUrl && (
          <img
            style={{
              position: 'absolute',
              left: '5px',
              top: '5px',
              zIndex: 1,
              maxWidth: '100px',
              maxHeight: '100px',
              objectFit: 'contain',
            }}
            src={watermarkUrl}
            alt=""
          />
        )}

        {isVideoMedia ? (
          <HomeCardVideo
            src={character.animated_image_url}
            eager={mediaPriority}
            onLoadedData={handleMediaReady}
            onError={handleMediaError}
            className={clsx('absolute inset-0 h-full w-full object-cover')}
          />
        ) : isAnimatedImageMedia ? (
          // GIF/动图不用 next/image，避免内部 src/srcset 更新导致动画回到首帧
          <img
            className={clsx('absolute inset-0 h-full w-full object-cover')}
            src={imageMediaUrl}
            alt={character.nickname}
            loading={mediaPriority ? 'eager' : 'lazy'}
            fetchPriority={mediaPriority ? 'high' : undefined}
            onLoad={handleMediaReady}
            onError={handleMediaError}
          />
        ) : (
          <Image
            unoptimized
            className={clsx('object-cover')}
            src={imageMediaUrl}
            alt={character.nickname}
            fill={true}
            priority={mediaPriority}
            onLoad={handleMediaReady}
            onError={handleMediaError}
          />
        )}
      </LifecycleLink>

      <div className="pointer-events-none absolute inset-0 max-md:hidden">
        {/* 顶部渐变效果，用于让水印更清晰 */}
        {watermarkUrl && (
          <div className="absolute left-0 right-0 top-0 h-[30%] bg-gradient-to-b from-black/50 to-black/0" />
        )}
        {character.nsfw && (
          <div className="absolute right-2 top-2">
            <Image
              src="/images/home/card-nsfw.png"
              alt=""
              width={34}
              height={20}
            />
          </div>
        )}
        {character.character_type === 2 && (
          <div className="absolute left-2 top-2">
            <Image
              src="/images/home/card-story.png"
              alt=""
              width={20}
              height={20}
            />
          </div>
        )}
        <div ref={containerRef} className="flex h-full flex-col justify-end">
          <div
            ref={bgRef}
            className={clsx(
              'flex max-h-[100%] min-h-[50%] flex-col justify-end bg-gradient-to-b from-black/0 to-black pt-4 text-white transition-all duration-300'
            )}
          >
            <LifecycleLink
              href={cardHref}
              prefetch
              onMouseLeave={handleHoverEnd}
              className={clsx(
                'z-1 pointer-events-auto relative flex h-full flex-col justify-end px-3.5',
                styles['hover-parent'],
                isHoverExitDelayed && styles['hover-parent-active']
              )}
            >
              <div className="flex items-center gap-2">
                <span className="truncate text-base font-medium leading-6">
                  {character.nickname}
                </span>
                <CharacterWinnerBadgeGuard badges={character.badges}>
                  {(winnerBadge) => (
                    <Tooltip
                      color="rgba(0, 0, 0, 0.5)"
                      overlayInnerStyle={{
                        fontSize: '12px',
                        backgroundColor: 'rgba(0,0,0,0.5)',
                        boxShadow: '0px 2px 4px 0px rgba(255,255,255,0.05)',
                        borderRadius: '8px',
                        margin: '0px 14px',
                      }}
                      getPopupContainer={(triggerNode: HTMLElement) => {
                        return (
                          (triggerNode.parentNode as HTMLElement) ||
                          document.body
                        )
                      }}
                      title={
                        <div className="flex gap-1.5">
                          <CharacterWinnerBadgeIcon
                            badges={character.badges}
                            width={18}
                            height={18}
                          />
                          <span className="text-xs">
                            {winnerBadge.display_name}
                          </span>
                        </div>
                      }
                    >
                      <div className="relative flex h-6 min-w-0 flex-shrink-0 items-center justify-center px-1 py-0.5 text-[13px]">
                        <CharacterWinnerBadgeIcon
                          badges={character.badges}
                          width={18}
                          height={18}
                        />
                        {/* <div className="absolute -right-4 -top-4">
                          <HomeStarIcon />
                        </div> */}
                      </div>
                    </Tooltip>
                  )}
                </CharacterWinnerBadgeGuard>

                {character.voice_supported && (
                  <Image
                    src="/images/home/card-voice.png"
                    alt=""
                    width={16}
                    height={16}
                    className="block shrink-0 object-contain"
                  />
                )}
              </div>
              {!hideDescriptionPreview && (
                <motion.div onMouseEnter={handleHoverStart}>
                  <div className="mt-[5px] overflow-hidden pb-2.5 text-xs">
                    <div
                      ref={textRef}
                      className={clsx(
                        styles['ellipsis-3-line'],
                        'text-white/60'
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {descriptionPreview}
                    </div>
                  </div>
                </motion.div>
              )}
            </LifecycleLink>
            <svg width="100%" height="1">
              <line
                x1="0"
                y1="0"
                x2="100%"
                y2="0"
                style={{
                  stroke: 'rgba(255, 255, 255, 0.3)',
                  strokeWidth: 2,
                  strokeDasharray: '3, 3',
                }}
              />
            </svg>
            <div
              className="pointer-events-auto flex h-[40px] cursor-auto items-center justify-between px-3.5 py-2.5 text-sm text-white"
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
            >
              <LifecycleLink
                href={withLocale(
                  `/profile/${character.creator_id}?tab=Characters`,
                  lang
                )}
                prefetch
                className="flex h-[26px] min-w-0 flex-1 items-center gap-1"
              >
                <Avatar
                  src={transformImage(creator.avatar_url, 56)}
                  alt={t('Creator')}
                  size={26}
                />
                <span className="w-fit flex-1 truncate text-sm leading-4 text-white">
                  @{creator.nickname}
                </span>
              </LifecycleLink>

              <div className="flex h-[26px] shrink-0 items-center">
                <div className="inline-flex items-center gap-0.5 text-xs leading-4 text-white">
                  <Image
                    src={
                      isGame
                        ? '/images/home/card-world-interaction.png'
                        : '/images/home/card-chat-message.png'
                    }
                    alt=""
                    width={16}
                    height={16}
                    className="ml-1 block shrink-0 object-contain"
                  />
                  <span className="leading-4">
                    {formatNumber(Number(characterStats.total_messages) || 0)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
